<script setup lang="ts">
/**
 * CampaignStickyBar — barre flottante mobile (bottom) : ring de progression +
 * montant collecté + dernier donateur + CTAs (Je participe / Partager).
 *
 * Composant PARTAGÉ (@donoor/ui) consommé en SOURCE par dashboard.donoor.org
 * et donoor.org. Couleurs en hex en dur (pas de token de thème) → rendu
 * identique dans les 2 apps malgré leurs thèmes différents.
 *
 * Le composant ne connaît PAS la destination du don : il émet `donate` et
 * chaque page décide (public → naviguer, preview → simuler). Idem `openShare`.
 *
 * `<UIcon>` reste global (résolu par l'app Nuxt qui transpile ce SFC via
 * build.transpile).
 *
 * USAGE (auto-import via wrapper local) :
 *   import CampaignStickyBar from "@donoor/ui/campaign/CampaignStickyBar.vue";
 *
 * Karim 2026-06-21 (factorisation cagnotte).
 */
import type { Campaign, CampaignDonor } from "./types";
import { useAnimatedNumber } from "@donoor/composables";

// Internationalisation (lot "paquets partagés", 2026-10-11) : clés `ui.*`
// et `commun.*` (bouton Partager), présentes dans donoor.org ET dashboard.
const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    campaign: Campaign;
    progressPercent: number;
    isCampaignClosed: boolean;
    isCampaignPaused?: boolean;
    hasGoal: boolean;
    formatAmountShort: (amount: number) => string;
    formatAmountCompact?: (amount: number | null | undefined) => string;
    visible: boolean;
    donors?: CampaignDonor[];
  }>(),
  { isCampaignPaused: false, donors: () => [], formatAmountCompact: undefined }
);

const emit = defineEmits<{
  openShare: [];
  donate: [];
}>();

const { displayValue: animatedAmount, setValue: setAmount } = useAnimatedNumber(props.campaign.raised || 0, 500);
const { displayValue: animatedProgress, setValue: setProgress } = useAnimatedNumber(props.progressPercent || 0, 500);

watch(() => props.campaign.raised, (newVal) => { setAmount(newVal || 0); }, { immediate: true });
watch(() => props.progressPercent, (newVal) => { setProgress(newVal || 0); }, { immediate: true });

const latestDonor = computed(() => props.donors[0] || null);

function defaultAmountCompact(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return "";
  const n = Number(amount);
  if (!Number.isFinite(n)) return "";
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return `${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1).replace(".0", "")}M €`;
  }
  if (n >= 1_000) {
    const k = n / 1_000;
    return `${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1).replace(".0", "")}k €`;
  }
  return `${Math.round(n)} €`;
}
const amountCompact = (a: number | null | undefined) =>
  props.formatAmountCompact ? props.formatAmountCompact(a) : defaultAmountCompact(a);

// Progress ring SVG
const RING_SIZE = 72;
const RING_STROKE = 9;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
const ringDashOffset = computed(
  () => RING_CIRCUMFERENCE - (animatedProgress.value / 100) * RING_CIRCUMFERENCE
);
</script>

<template>
  <Transition name="slide-up">
    <div
      v-if="visible && !isCampaignClosed && !isCampaignPaused"
      class="fixed bottom-3 left-3 right-3 z-50 lg:hidden"
    >
      <div
        class="bg-white rounded-3xl shadow-[0_20px_60px_-10px_rgba(0,0,0,0.35)] border border-gray-100 px-4 py-3.5"
      >
        <!-- Top: ring + montant + dernier donateur -->
        <div class="flex items-center gap-3 mb-3">
          <!-- Ring -->
          <div v-if="hasGoal" class="relative shrink-0">
            <svg :width="RING_SIZE" :height="RING_SIZE" class="-rotate-90">
              <defs>
                <linearGradient id="sticky-progress-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#1f9afe" />
                  <stop offset="100%" stop-color="#1366B5" />
                </linearGradient>
              </defs>
              <circle
                :cx="RING_SIZE / 2"
                :cy="RING_SIZE / 2"
                :r="RING_RADIUS"
                stroke="#e5e7eb"
                :stroke-width="RING_STROKE"
                fill="none"
              />
              <circle
                :cx="RING_SIZE / 2"
                :cy="RING_SIZE / 2"
                :r="RING_RADIUS"
                stroke="url(#sticky-progress-gradient)"
                :stroke-width="RING_STROKE"
                fill="none"
                stroke-linecap="round"
                :stroke-dasharray="RING_CIRCUMFERENCE"
                :stroke-dashoffset="ringDashOffset"
                class="transition-all duration-500"
              />
            </svg>
            <span class="absolute inset-0 flex items-center justify-center text-xs font-bold text-gray-700">
              {{ Math.round(animatedProgress) }}%
            </span>
          </div>

          <div class="min-w-0 flex-1">
            <p class="text-lg xs:text-xl text-gray-900 leading-tight text-balance">
              <!-- Montant collecté : compact sous xs (184 447 € → 184 k €) pour
                   ne pas déborder sur petits écrans, valeur pleine dès xs. -->
              <span class="font-black whitespace-nowrap xs:hidden" style="font-weight: 1000; font-stretch: 110%">{{ amountCompact(animatedAmount) }}</span>
              <span class="font-black whitespace-nowrap hidden xs:inline" style="font-weight: 1000; font-stretch: 110%">{{ formatAmountShort(animatedAmount) }}</span>
              <span class="text-gray-500 font-semibold text-sm xs:text-base ml-1">{{ t("ui.commun.collectes") }}</span>
              <template v-if="hasGoal">
                <span class="text-gray-500 font-semibold text-sm xs:text-base ml-1">{{ t("ui.commun.sur") }}</span>
                <span class="font-black whitespace-nowrap" style="font-weight: 1000; font-stretch: 110%">{{ ' ' + amountCompact(campaign.goal_amount) }}</span>
              </template>
            </p>
            <p
              v-if="latestDonor"
              class="text-sm text-gray-500 truncate mt-1"
            >
              <span class="font-semibold text-gray-700">
                {{ latestDonor.is_anonymous ? t("ui.commun.anonyme") : latestDonor.name }}
              </span>
              {{ t("ui.commun.aDonne") }}
              <span class="font-semibold text-gray-700">{{ formatAmountShort(latestDonor.amount) }}</span>
            </p>
          </div>
        </div>

        <!-- Bottom: CTAs -->
        <div class="grid grid-cols-2 gap-2.5">
          <button
            type="button"
            class="py-3 xs:py-3.5 rounded-full text-base font-bold bg-[#0F3D8C] hover:bg-[#0A2D6B] active:scale-[0.98] text-white transition-all"
            @click="emit('donate')"
          >
            {{ t("ui.commun.jeParticipe") }}
          </button>
          <button
            type="button"
            class="py-3 xs:py-3.5 rounded-full text-base font-bold bg-[#D7EBFF] hover:bg-[#A8D2FE] active:scale-[0.98] text-[#0F3D8C] transition-all"
            @click="emit('openShare')"
          >
            {{ t("commun.actions.partager") }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.3s ease;
}
.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(120%);
  opacity: 0;
}
</style>
